/** Bus / train trip shared theme + helpers. */
import type { GroundMode, GroundTrip } from '../../types/ground';

export type { GroundMode, GroundTrip };

type Theme = {
  accent: string;
  selectedBorder: string;
  softBorder: string;
  badge: string;
  addBtn: string;
  filterActive: string;
  filterBadge: string;
  panel: string;
  applyBtn: string;
  iconBg: string;
  routeDotFrom: string;
  routeDotTo: string;
  routeLine: string;
  price: string;
};

export const THEMES: Record<GroundMode, Theme> = {
  bus: {
    accent: 'violet',
    selectedBorder: 'border-violet-400 dark:border-violet-500',
    softBorder: 'border-stone-200 dark:border-slate-600',
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
    addBtn: 'bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-violet-500/20',
    filterActive: 'bg-violet-100 dark:bg-violet-900/40 border-violet-300 dark:border-violet-700 text-violet-800 dark:text-violet-200',
    filterBadge: 'bg-violet-600',
    panel: 'border-violet-200/80 dark:border-slate-600 bg-gradient-to-br from-violet-50/90 to-white dark:from-slate-800/80 dark:to-slate-900/60',
    applyBtn: 'bg-violet-600 hover:bg-violet-700',
    iconBg: 'bg-violet-600',
    routeDotFrom: 'bg-violet-500',
    routeDotTo: 'bg-fuchsia-500',
    routeLine: 'border-violet-400/50',
    price: 'text-violet-600 dark:text-violet-400',
  },
  train: {
    accent: 'indigo',
    selectedBorder: 'border-indigo-400 dark:border-indigo-500',
    softBorder: 'border-stone-200 dark:border-slate-600',
    badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
    addBtn: 'bg-gradient-to-r from-indigo-500 to-slate-700 hover:from-indigo-600 hover:to-slate-800 shadow-indigo-500/20',
    filterActive: 'bg-indigo-100 dark:bg-indigo-900/40 border-indigo-300 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200',
    filterBadge: 'bg-indigo-600',
    panel: 'border-indigo-200/80 dark:border-slate-600 bg-gradient-to-br from-indigo-50/90 to-white dark:from-slate-800/80 dark:to-slate-900/60',
    applyBtn: 'bg-indigo-600 hover:bg-indigo-700',
    iconBg: 'bg-indigo-600',
    routeDotFrom: 'bg-indigo-500',
    routeDotTo: 'bg-slate-500',
    routeLine: 'border-indigo-400/50',
    price: 'text-indigo-600 dark:text-indigo-400',
  },
  ferry: {
    accent: 'cyan',
    selectedBorder: 'border-cyan-400 dark:border-cyan-500',
    softBorder: 'border-stone-200 dark:border-slate-600',
    badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-200',
    addBtn: 'bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 shadow-cyan-500/20',
    filterActive: 'bg-cyan-100 dark:bg-cyan-900/40 border-cyan-300 dark:border-cyan-700 text-cyan-800 dark:text-cyan-200',
    filterBadge: 'bg-cyan-600',
    panel: 'border-cyan-200/80 dark:border-slate-600 bg-gradient-to-br from-cyan-50/90 to-white dark:from-slate-800/80 dark:to-slate-900/60',
    applyBtn: 'bg-cyan-600 hover:bg-cyan-700',
    iconBg: 'bg-cyan-600',
    routeDotFrom: 'bg-cyan-500',
    routeDotTo: 'bg-teal-500',
    routeLine: 'border-cyan-400/50',
    price: 'text-cyan-600 dark:text-cyan-400',
  },
};

export function emptyTrip(mode: GroundMode): Partial<GroundTrip> {
  return {
    mode,
    date: new Date().toISOString().slice(0, 10),
    tag: '',
    originCity: '',
    destinationCity: '',
    origin: '',
    destination: '',
    departure_time: '',
    arrival_time: '',
    operator: '',
    routeNumber: '',
    trainName: '',
    distance: undefined,
    duration: undefined,
    formattedDuration: '',
    price: undefined,
    notes: '',
  };
}

export type GroundEditPayload = {
  mode: GroundMode;
  date: string;
  tag: string | null;
  originCity: string;
  destinationCity: string;
  origin: string | null;
  destination: string | null;
  departure_time: string | null;
  arrival_time: string | null;
  operator: string | null;
  routeNumber: string | null;
  trainName: string | null;
  distance: number | null;
  duration: number | null;
  formattedDuration: string | null;
  price: number | null;
  notes: string | null;
  originSubdivision: string | null;
  destinationSubdivision: string | null;
  trainNameEn: string | null;
};

export function buildGroundEditPayload(
  mode: GroundMode,
  draft: Partial<GroundTrip>,
): GroundEditPayload {
  return {
    mode,
    date: draft.date || '',
    tag: draft.tag?.trim() || null,
    originCity: (draft.originCity || '').trim(),
    destinationCity: (draft.destinationCity || '').trim(),
    origin: draft.origin?.trim() || null,
    destination: draft.destination?.trim() || null,
    departure_time: draft.departure_time?.trim() || null,
    arrival_time: draft.arrival_time?.trim() || null,
    operator: draft.operator?.trim() || null,
    routeNumber: draft.routeNumber?.trim() || null,
    trainName: mode === 'train' ? draft.trainName?.trim() || null : null,
    trainNameEn: mode === 'train' ? draft.trainNameEn?.trim() || null : null,
    distance: draft.distance ?? null,
    duration: draft.duration ?? formattedToMinutes(draft.formattedDuration) ?? null,
    formattedDuration:
      draft.formattedDuration?.trim() || minutesToFormatted(draft.duration) || null,
    price: draft.price ?? null,
    notes: mode === 'train' ? null : draft.notes?.trim() || null,
    originSubdivision: draft.originSubdivision || null,
    destinationSubdivision: draft.destinationSubdivision || null,
  };
}

function groundFieldEqual(
  key: keyof GroundEditPayload,
  left: GroundEditPayload,
  right: GroundEditPayload,
): boolean {
  const a = left[key];
  const b = right[key];
  if (typeof a === 'number' || typeof b === 'number') {
    return Number(a ?? 0) === Number(b ?? 0);
  }
  return (a ?? '') === (b ?? '');
}

/** Only changed fields for PUT — avoids overwriting / re-triggering geo on time-only edits. */
export function groundEditDelta(
  mode: GroundMode,
  original: GroundTrip,
  draft: Partial<GroundTrip>,
): Partial<GroundEditPayload> {
  const before = buildGroundEditPayload(mode, original);
  const after = buildGroundEditPayload(mode, draft);
  const delta: Partial<GroundEditPayload> = {};
  (Object.keys(before) as (keyof GroundEditPayload)[]).forEach((key) => {
    if (key === 'mode') return;
    if (!groundFieldEqual(key, before, after)) {
      (delta as Record<string, unknown>)[key] = after[key];
    }
  });
  return delta;
}

export function minutesToFormatted(min: number | null | undefined): string {
  if (min == null || !Number.isFinite(min) || min <= 0) return '';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** Extract intermediate stops from free-text notes. */
export function parseGroundStops(notes?: string | null): string[] {
  if (!notes?.trim()) return [];
  const text = notes.trim();
  const arrowParts = text
    .split(/\s*(?:→|->|—|–)\s*/)
    .map(s => s.trim())
    .filter(Boolean);
  if (arrowParts.length >= 3) {
    return arrowParts.slice(1, -1);
  }
  const lines = text
    .split(/[\n;]+/)
    .map(s => s.replace(/^\s*[-•*\d.)]+\s*/, '').trim())
    .filter(s => s.length > 1 && !/^https?:/i.test(s));
  if (lines.length >= 2) return lines;
  const commas = text.split(/\s*,\s*/).map(s => s.trim()).filter(s => s.length > 1);
  if (commas.length >= 2) return commas;
  return [];
}

/** Allow line wrap after hyphens/dashes in long station names. */
export function softBreakAfterHyphens(text: string): string {
  return text.replace(/([\-−–—])/g, '$1\u200B');
}

export function formattedToMinutes(s: string | null | undefined): number | undefined {
  if (!s?.trim()) return undefined;
  const m = s.trim().match(/^(\d+)\s*:\s*(\d{1,2})$/);
  if (!m) return undefined;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}
